import clsx from 'clsx';
import {
  InputHTMLAttributes,
  MutableRefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { measureTextWidth } from 'utils/helpers';

export type TextProps = {
  value?: string;
  onChange?: (value: string) => void;
  onInput?: (value: string) => void;
  debounce?: number;
  autoFocus?: boolean;
  autoSelect?: boolean;
  size?: 'sm' | 'md';
  controlled?: boolean;
  prefix?: string;
  suffix?: string;
  invalid?: boolean;
  inputRef?: MutableRefObject<HTMLElement | null>;
} & Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'onInput' | 'autoFocus' | 'size'
>;

export default function Text({
  value: initialValue,
  onChange,
  onInput,
  debounce,
  autoFocus,
  autoSelect,
  size = 'md',
  controlled,
  prefix,
  suffix,
  invalid,
  inputRef,
  ...props
}: TextProps) {
  const [value, setValue] = useState(initialValue ?? '');
  const [prefixWidth, setPrefixWidth] = useState(0);
  const [valueWidth, setValueWidth] = useState(0);
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setValue(initialValue ?? '');
  }, [initialValue]);

  useEffect(() => {
    if (autoFocus && ref.current) {
      ref.current.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    if (autoSelect && ref.current) {
      ref.current.select();
    }
  }, [autoSelect]);

  useLayoutEffect(() => {
    const forwardedRef = inputRef;
    if (!forwardedRef) {
      return undefined;
    }
    forwardedRef.current = ref.current;
    return () => {
      forwardedRef.current = null;
    };
  });

  useEffect(() => {
    if (prefix) {
      setPrefixWidth(measureTextWidth(prefix, 14));
    }
  }, [prefix]);

  useEffect(() => {
    setValueWidth(measureTextWidth(value, 14));
    const timeout = setTimeout(() => {
      if (onChange && value !== (initialValue ?? '')) {
        onChange(value);
      }
    }, debounce);
    return () => clearTimeout(timeout);
  }, [value]);

  const classes = clsx(
    'block w-full rounded-md border-gray-300 shadow-sm placeholder:text-gray-400 focus:border-blue-500 focus:ring-blue-500 text-sm',
    props.disabled && 'opacity-[70%] cursor-not-allowed',
    size === 'sm' && 'px-2 py-1',
    size === 'md' && 'px-3 py-2',
    invalid && 'ring-2 ring-red-500'
  );

  return (
    <div className='relative w-full'>
      <input
        {...props}
        ref={ref}
        type='text'
        className={classes}
        value={controlled ? initialValue : value}
        onChange={(e) => setValue(e.target.value)}
        onInput={(e) => onInput && onInput(e.currentTarget.value)}
        style={
          prefix && value !== ''
            ? { paddingLeft: (size === 'sm' ? 8 : 12) + prefixWidth }
            : undefined
        }
      />
      <div className='pointer-events-none absolute inset-0 overflow-hidden'>
        {value !== '' && prefix && (
          <span
            className='pointer-events-none absolute inset-y-0 flex items-center whitespace-pre text-sm'
            style={{ left: size === 'sm' ? 8 : 12 }}
          >
            {prefix}
          </span>
        )}
        {value !== '' && suffix && (
          <span
            className='pointer-events-none absolute inset-y-0 flex items-center whitespace-pre text-sm'
            style={{
              left: (size === 'sm' ? 8 : 12) + valueWidth + prefixWidth
            }}
          >
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}
